import type { Point, PointerEventObject } from 'highcharts';
import { useCallback, useState } from 'react';

import type { CompanyType } from '@/api/common';
import type { FlareChart } from '@/components/Flare';
import { getNearestPointFromXValue } from '@/helper/highcharts-util';

import CompanyDetailNonDigitalEventsChart from './CompanyDetailNonDigitalEventsChart';
import CompanyDetailTrendChart from './CompanyDetailTrendChart';

const CHART_MARGIN_RIGHT = 160;
const CHART_MARGIN_LEFT = 100;

type Props = {
  company?: CompanyType;
};

const CompanyDetailTrendComboCharts = ({ company }: Props) => {
  const [trendChart, setTrendChart] = useState<FlareChart>();
  const [nonDigitalEventsChart, setNonDigitalEventsChart] = useState<FlareChart>();

  const drawCrosshair = (
    chart: FlareChart | undefined,
    event: PointerEventObject,
    pointX: number,
  ) => {
    if (chart?.xAxis?.[0]) {
      const nearestPoint = getNearestPointFromXValue(chart, pointX);

      if (nearestPoint) {
        chart.xAxis[0].drawCrosshair(event, nearestPoint);
      } else {
        chart.xAxis[0].hideCrosshair();
      }
    }
  };

  const handleHoverChart = useCallback(
    (event: PointerEventObject, point: Point) => {
      drawCrosshair(trendChart, event, point.x);
      drawCrosshair(nonDigitalEventsChart, event, point.x);
    },
    [trendChart, nonDigitalEventsChart],
  );

  const handleHoverLeaveChart = useCallback(() => {
    if (trendChart?.xAxis?.[0]) {
      trendChart.xAxis[0].hideCrosshair();
    }

    if (nonDigitalEventsChart?.xAxis?.[0]) {
      nonDigitalEventsChart.xAxis[0].hideCrosshair();
    }
  }, [trendChart, nonDigitalEventsChart]);

  return (
    <div className="flex flex-col gap-6">
      <CompanyDetailTrendChart
        company={company}
        marginLeft={CHART_MARGIN_LEFT}
        marginRight={CHART_MARGIN_RIGHT}
        onHoverChart={handleHoverChart}
        onHoverLeaveChart={handleHoverLeaveChart}
        onPostRenderChart={setTrendChart}
      />
      <CompanyDetailNonDigitalEventsChart
        company={company}
        marginLeft={CHART_MARGIN_LEFT}
        marginRight={CHART_MARGIN_RIGHT}
        onHoverChart={handleHoverChart}
        onHoverLeaveChart={handleHoverLeaveChart}
        onPostRenderChart={setNonDigitalEventsChart}
      />
    </div>
  );
};

export default CompanyDetailTrendComboCharts;
